/**
 * Maps Google Business Profile primary categories to buyer-intent search keywords.
 * Used by the market keyword resolver to generate candidate scrape queries.
 */

const CATEGORY_KEYWORD_ENTRIES = [
  {
    patterns: [/junk removal/i, /hauling/i, /debris removal/i],
    primary: 'junk removal',
    alternates: [
      'junk removal service',
      'furniture removal',
      'appliance removal',
      'hauling service',
      'trash removal',
      'debris removal',
      'commercial junk removal',
    ],
  },
  {
    patterns: [/hvac/i, /heating/i, /air conditioning/i, /furnace/i],
    primary: 'HVAC contractor',
    alternates: [
      'HVAC repair',
      'AC repair',
      'furnace repair',
      'heating and cooling',
      'air conditioning repair',
    ],
  },
  {
    patterns: [/plumb/i, /drain/i, /sewer/i, /water heater/i],
    primary: 'plumber',
    alternates: [
      'plumbing company',
      'emergency plumber',
      'drain cleaning',
      'water heater repair',
      'sewer repair',
    ],
  },
  {
    patterns: [/med spa/i, /medical spa/i, /aesthetic/i, /botox/i, /laser hair/i],
    primary: 'med spa',
    alternates: [
      'medical spa',
      'botox',
      'laser hair removal',
      'skin care clinic',
      'aesthetic clinic',
    ],
  },
  {
    patterns: [/electrician/i, /electrical/i],
    primary: 'electrician',
    alternates: ['electrical contractor', 'emergency electrician', 'electrical repair'],
  },
  {
    patterns: [/roof/i],
    primary: 'roofer',
    alternates: ['roofing company', 'roof repair', 'roof replacement'],
  },
  {
    patterns: [/landscap/i, /lawn/i, /yard/i],
    primary: 'landscaper',
    alternates: ['landscaping company', 'lawn care', 'lawn service', 'yard maintenance'],
  },
  {
    patterns: [/pest control/i, /exterminator/i],
    primary: 'pest control',
    alternates: ['exterminator', 'termite control', 'rodent control'],
  },
  {
    patterns: [/dentist/i, /dental/i],
    primary: 'dentist',
    alternates: ['dental clinic', 'family dentist', 'cosmetic dentist'],
  },
  {
    patterns: [/chiropractor/i],
    primary: 'chiropractor',
    alternates: ['chiropractic clinic', 'back pain chiropractor'],
  },
  {
    patterns: [/cleaning service/i, /house cleaning/i, /maid/i],
    primary: 'cleaning service',
    alternates: ['house cleaning', 'maid service', 'home cleaning'],
  },
  {
    patterns: [/garage door/i],
    primary: 'garage door repair',
    alternates: ['garage door service', 'garage door installation'],
  },
  {
    patterns: [/tree service/i, /arborist/i],
    primary: 'tree service',
    alternates: ['tree removal', 'tree trimming', 'arborist'],
  },
  {
    patterns: [/moving company/i, /mover/i],
    primary: 'moving company',
    alternates: ['movers', 'local movers', 'moving service'],
  },
  {
    patterns: [/dog walker/i, /pet sitter/i, /dog boarding/i],
    primary: 'dog walker',
    alternates: ['pet sitter', 'dog boarding', 'pet care'],
  },
  {
    patterns: [/auto repair/i, /mechanic/i],
    primary: 'auto repair',
    alternates: ['mechanic', 'car repair', 'auto shop'],
  },
  {
    patterns: [/painter/i, /painting/i],
    primary: 'house painter',
    alternates: ['painting contractor', 'interior painter', 'exterior painter'],
  },
  {
    patterns: [/general contractor/i, /construction company/i, /contractor/i],
    primary: 'general contractor',
    alternates: ['construction company', 'home builder', 'remodeling contractor'],
  },
  {
    patterns: [/locksmith/i],
    primary: 'locksmith',
    alternates: ['emergency locksmith', 'lock repair'],
  },
  {
    patterns: [/pool/i],
    primary: 'pool service',
    alternates: ['pool cleaning', 'pool repair', 'pool contractor'],
  },
];

const GENERIC_SUFFIXES = /\b(inc|llc|l\.l\.c\.|co|company|services|service|group|nashville|local|pro|pros)\b/gi;

/**
 * Normalize a GMB category label into a short service keyword phrase.
 */
export function normalizeCategoryLabel(label) {
  if (!label) return '';
  return label
    .replace(/\s+service$/i, '')
    .replace(/\s+company$/i, '')
    .replace(/\s+contractor$/i, ' contractor')
    .trim()
    .toLowerCase();
}

/**
 * Look up canonical keywords for a GMB primary category.
 */
export function getKeywordsForCategory(primaryCategory) {
  const label = (primaryCategory || '').trim();
  if (!label) {
    return { primary: '', alternates: [] };
  }

  for (const entry of CATEGORY_KEYWORD_ENTRIES) {
    if (entry.patterns.some((pattern) => pattern.test(label))) {
      return { primary: entry.primary, alternates: [...entry.alternates] };
    }
  }

  const normalized = normalizeCategoryLabel(label);
  return {
    primary: normalized,
    alternates: normalized ? [`${normalized} service`, `${normalized} company`] : [],
  };
}

/**
 * Extract service-like tokens from a business name.
 */
export function extractKeywordsFromBusinessName(name) {
  if (!name) return [];
  const cleaned = name
    .replace(GENERIC_SUFFIXES, ' ')
    .replace(/[^a-zA-Z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  if (!cleaned) return [];
  const words = cleaned.split(' ').filter((word) => word.length > 2);
  if (words.length <= 2) {
    return [cleaned];
  }
  return [];
}
